import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, 
  Minus, 
  Target, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Save,
  Download
} from "lucide-react";
import NavBar from "@/components/NavBar";

interface SWOTItem {
  id: string;
  text: string;
  category: 'strength' | 'weakness' | 'opportunity' | 'threat';
  impact: 'high' | 'medium' | 'low';
}

export default function SWOTAnalysis() {
  const [swotItems, setSWOTItems] = useState<SWOTItem[]>([]);
  const [newItems, setNewItems] = useState({
    strength: "",
    weakness: "",
    opportunity: "",
    threat: ""
  });

  const addItem = (category: 'strength' | 'weakness' | 'opportunity' | 'threat') => {
    const text = newItems[category];
    if (text.trim()) {
      const newItem: SWOTItem = {
        id: Date.now().toString(),
        text: text.trim(),
        category,
        impact: 'medium'
      };
      setSWOTItems([...swotItems, newItem]);
      setNewItems({ ...newItems, [category]: "" });
    }
  };

  const removeItem = (id: string) => {
    setSWOTItems(swotItems.filter(item => item.id !== id));
  };

  const updateImpact = (id: string, impact: 'high' | 'medium' | 'low') => {
    setSWOTItems(swotItems.map(item => 
      item.id === id ? { ...item, impact } : item
    ));
  };

  const getItemsByCategory = (category: 'strength' | 'weakness' | 'opportunity' | 'threat') => {
    return swotItems.filter(item => item.category === category);
  };

  const getImpactColor = (impact: 'high' | 'medium' | 'low') => {
    switch (impact) {
      case 'high': return 'bg-red-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const swotCategories = [
    {
      key: 'strength' as const,
      title: 'Strengths',
      description: 'Internal positive factors that give advantage',
      icon: TrendingUp,
      color: 'border-green-200 bg-green-50 dark:bg-green-950',
      textColor: 'text-green-800 dark:text-green-200'
    },
    {
      key: 'weakness' as const,
      title: 'Weaknesses', 
      description: 'Internal negative factors that need improvement',
      icon: TrendingDown,
      color: 'border-red-200 bg-red-50 dark:bg-red-950',
      textColor: 'text-red-800 dark:text-red-200'
    },
    {
      key: 'opportunity' as const,
      title: 'Opportunities',
      description: 'External positive factors to leverage',
      icon: Target,
      color: 'border-blue-200 bg-blue-50 dark:bg-blue-950',
      textColor: 'text-blue-800 dark:text-blue-200'
    },
    {
      key: 'threat' as const,
      title: 'Threats',
      description: 'External negative factors to mitigate',
      icon: AlertTriangle,
      color: 'border-orange-200 bg-orange-50 dark:bg-orange-950',
      textColor: 'text-orange-800 dark:text-orange-200'
    }
  ];

  const generateStrategies = () => {
    const strengths = getItemsByCategory('strength');
    const weaknesses = getItemsByCategory('weakness');
    const opportunities = getItemsByCategory('opportunity');
    const threats = getItemsByCategory('threat');

    return {
      so: { // Strengths-Opportunities
        title: "Growth Strategies (SO)",
        description: "Use strengths to maximize opportunities",
        count: strengths.length + opportunities.length
      },
      wo: { // Weaknesses-Opportunities
        title: "Development Strategies (WO)", 
        description: "Overcome weaknesses by leveraging opportunities",
        count: weaknesses.length + opportunities.length
      },
      st: { // Strengths-Threats
        title: "Defensive Strategies (ST)",
        description: "Use strengths to minimize threats",
        count: strengths.length + threats.length
      },
      wt: { // Weaknesses-Threats
        title: "Survival Strategies (WT)",
        description: "Minimize weaknesses and avoid threats",
        count: weaknesses.length + threats.length
      }
    };
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">SWOT Analysis</h1>
            <p className="text-muted-foreground">
              Strategic planning tool to evaluate Strengths, Weaknesses, Opportunities, and Threats
            </p>
          </div>

          <Tabs defaultValue="analysis" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="analysis">SWOT Analysis</TabsTrigger>
              <TabsTrigger value="matrix">SWOT Matrix</TabsTrigger>
              <TabsTrigger value="strategies">Strategic Options</TabsTrigger>
            </TabsList>

            <TabsContent value="analysis" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {swotCategories.map((category) => {
                  const Icon = category.icon;
                  const items = getItemsByCategory(category.key);
                  
                  return (
                    <Card key={category.key} className={category.color}>
                      <CardHeader>
                        <CardTitle className={`flex items-center gap-2 ${category.textColor}`}>
                          <Icon className="h-5 w-5" />
                          {category.title}
                        </CardTitle>
                        <CardDescription className={category.textColor}>
                          {category.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex gap-2">
                          <Textarea
                            placeholder={`Add ${category.title.toLowerCase().slice(0, -1)}...`}
                            value={newItems[category.key]}
                            onChange={(e) => setNewItems({ 
                              ...newItems, 
                              [category.key]: e.target.value 
                            })}
                            className="bg-background"
                          />
                          <Button
                            onClick={() => addItem(category.key)}
                            disabled={!newItems[category.key].trim()}
                            size="sm"
                            className="mt-auto"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          {items.map((item) => (
                            <div key={item.id} className="bg-background rounded-lg p-3 border">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm flex-1">{item.text}</p>
                                <div className="flex items-center gap-2">
                                  <select
                                    value={item.impact}
                                    onChange={(e) => updateImpact(item.id, e.target.value as 'high' | 'medium' | 'low')}
                                    className="text-xs border rounded px-2 py-1"
                                  >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                  </select>
                                  <Badge className={getImpactColor(item.impact)}>
                                    {item.impact}
                                  </Badge>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => removeItem(item.id)}
                                  >
                                    <Minus className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="matrix" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>SWOT Matrix Overview</CardTitle>
                  <CardDescription>
                    Visual representation of all SWOT factors with impact levels
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 h-96">
                    {/* Strengths */}
                    <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50 dark:bg-green-950">
                      <h3 className="font-semibold text-green-800 dark:text-green-200 mb-3">
                        Strengths ({getItemsByCategory('strength').length})
                      </h3>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {getItemsByCategory('strength').map((item) => (
                          <div key={item.id} className="bg-background rounded p-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="flex-1">{item.text}</span>
                              <Badge className={getImpactColor(item.impact)}>
                                {item.impact}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Weaknesses */}
                    <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50 dark:bg-red-950">
                      <h3 className="font-semibold text-red-800 dark:text-red-200 mb-3">
                        Weaknesses ({getItemsByCategory('weakness').length})
                      </h3>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {getItemsByCategory('weakness').map((item) => (
                          <div key={item.id} className="bg-background rounded p-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="flex-1">{item.text}</span>
                              <Badge className={getImpactColor(item.impact)}>
                                {item.impact}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Opportunities */}
                    <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 dark:bg-blue-950">
                      <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-3">
                        Opportunities ({getItemsByCategory('opportunity').length})
                      </h3>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {getItemsByCategory('opportunity').map((item) => (
                          <div key={item.id} className="bg-background rounded p-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="flex-1">{item.text}</span>
                              <Badge className={getImpactColor(item.impact)}>
                                {item.impact}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Threats */}
                    <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50 dark:bg-orange-950">
                      <h3 className="font-semibold text-orange-800 dark:text-orange-200 mb-3">
                        Threats ({getItemsByCategory('threat').length})
                      </h3>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {getItemsByCategory('threat').map((item) => (
                          <div key={item.id} className="bg-background rounded p-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="flex-1">{item.text}</span>
                              <Badge className={getImpactColor(item.impact)}>
                                {item.impact}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="strategies" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {Object.entries(generateStrategies()).map(([key, strategy]) => (
                  <Card key={key}>
                    <CardHeader>
                      <CardTitle>{strategy.title}</CardTitle>
                      <CardDescription>{strategy.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Badge variant="outline">
                          {strategy.count} factors to consider
                        </Badge>
                        <Textarea
                          placeholder={`Develop ${strategy.title.toLowerCase()} based on your SWOT analysis...`}
                          className="min-h-24"
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex gap-4">
                <Button className="flex-1">
                  <Save className="h-4 w-4 mr-2" />
                  Save Analysis
                </Button>
                <Button variant="outline" className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Export Report
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}